/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';
import {
    Box,
    Button,
    FlexCol,
    FlexRow,
    SearchActions as GaActions,
    TextField,
    useFlags,
    useToast,
} from 'sarsaparilla';
import cx from 'classnames';
import axios from 'axios';
import omit from 'lodash/omit';
import AiFeedbackModal from './AISearchModal';
import * as actions from '../../actions/search';
import * as types from '../../constants';
import { getParsedAiSearchCriterias } from '../../utils/aiSearchUtils';
import { useAppDispatch, useAppSelector } from '../../redux/redux-helpers';
import { UiFiltersProps } from '../../types/filters';

export default function AiSearchInput({
    isMobile,
}: {
    query?: string;
    isMobile?: boolean;
}) {
    const { enableAiSearchInput } = useFlags();
    const dispatch = useAppDispatch();
    const showToast = useToast();

    /** Input States */
    const search = useAppSelector((state) => state.search);
    const [isSurveyOpen, setIsSurveyOpen] = React.useState(false);
    const [inputValue, setInputValue] = React.useState(search?.ai_query || '');

    /**
     * If `ai_query` is cleared or updated, update the local input value accordingly.
     */
    React.useEffect(() => {
        if (search?.ai_query !== undefined) {
            setInputValue(search.ai_query);
        }
    }, [search?.ai_query]);

    /**
     * Fetch search criteria based on AI processing of the query
     */
    const fetchAiParseParams = async (query: string) => {
        try {
            const { data } = await axios.get(
                `${process.env.API}/search/parse?q=${query}`
            );
            const initialUiFilters: UiFiltersProps = {
                ...omit(types.SEARCH_INITIAL_STATE.uiFilters, [
                    'hasFilters',
                    'lastAppliedFilter',
                    'includeUnavailable',
                ]),
            };
            return getParsedAiSearchCriterias(data, initialUiFilters);
        } catch (error) {
            throw new Error('Failed to fetch AI criterias');
        }
    };

    const submitSearch = async () => {
        if (!inputValue) {
            return;
        }
        try {
            dispatch({ type: types.START_GET_SEARCH_RESULTS });

            const params = await fetchAiParseParams(inputValue);
            // Add the filtered parameters coming from the AI Parse endpoint
            dispatch(
                actions.updateSearchCriterias({
                    ...types.SEARCH_INITIAL_STATE,
                    ai_query: inputValue,
                    ...params,
                })
            );

            // Track search and update browser state
            GaActions.trackRunSearch(event, () => {
                const uri = actions.buildSearchNavUri('/search', { search: params });
                window.history.replaceState({}, '', uri);
                dispatch(actions.fetchSearchResults());
            });
        } catch {
            showToast('There was an error, try again later.', {
                type: 'error',
                delay: 5000,
            });
        } finally {
            dispatch({ type: types.END_GET_SEARCH_RESULTS });
        }
    };

    /**
     * Handles keyboard interactions for search submission
     * Prevents default form submission on Enter key unless Shift is pressed
     */
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            submitSearch();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);
    };

    /**
     * Handles form submission events
     * Prevents default form behavior and triggers search
     */
    const handleSubmitForm = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        submitSearch();
    };

    if (!enableAiSearchInput || search?.filtersVisible) {
        return null;
    }

    return (
        <div className={cx('ai-search-container', { 'is-mobile': isMobile })}>
            <Box className="ai-search-box" background="gray-2" border="gray">
                <FlexRow
                    alignItems="center"
                    justifyContent="space-between"
                    hasGutters={false}
                >
                    <FlexCol xs={12} lg={10}>
                        <form onSubmit={handleSubmitForm}>
                            <TextField
                                isInline
                                onKeyDown={handleKeyDown}
                                onChange={handleChange}
                                autoComplete="off"
                                value={inputValue}
                                id="ai-search-field"
                                className="ai-search-input"
                                label="AI Search Query"
                                placeholder="Tell us what kind of outdoor recreation you want to reserve. Be as descriptive as possible."
                            />
                        </form>
                    </FlexCol>
                    <FlexCol sm="variable">
                        <Button
                            appearance="link"
                            size="sm"
                            clickTagCategory="Search"
                            clickTagAction="AI Search Feedback Button"
                            clickTagLabel="Search Results"
                            aria-label={`Click to provide feedback to AI Search`}
                            onClick={() => setIsSurveyOpen(true)}
                        >
                            Provide Feedback on AI Search
                        </Button>
                    </FlexCol>
                </FlexRow>
            </Box>
            <AiFeedbackModal
                isSurveyOpen={isSurveyOpen}
                onCloseSurvey={() => setIsSurveyOpen(false)}
            />
        </div>
    );
}
